import { useLayoutEffect } from "react";
import { Checkbox } from "@privacy/ui";
import { IconButton, Stack, Typography } from "@mui/material";
import type { DataGridPremiumProps, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useTranslation } from "@privacy/translations";
import {
  gridFilteredDescendantCountLookupSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { useNavigate } from "react-router";
import type { TenantRolesHierarchy, Role } from "../../../../api/users.types";
import type { useUpdateRoleHierarchy } from "../../../../api/users";
import { useGetUserRolesHierarchy } from "../../../../api/users";
import { logUnhandled } from "../../../../api/logError";
import type { DisplayedRole } from "../../Roles";
import {
  DISPLAYED_ROLES,
  getRoleGridColumnWidth,
  RoleGridColumnHeader,
  ROLES_CONTROLLED_BY_ROOT,
  roleTexts,
} from "../../Roles";
import { TenantIcon2, ChevronRightIcon, PermissionIcon1 } from "../../../../shared/icons";
import { useScimEditingMode } from "../../shared/useScimEditingMode";
import { Drawer } from "../../../../shared/Drawer";
import { UserResponsibleWarningDialog } from "../UserResponsibleWarningDialog";
import { LosePermissionToUpdateRolesDialog } from "../LosePermissionToUpdateRolesDialog";
import { EXPANDED_SIDEBAR_WIDTH } from "../../../../root/layout/sidebarConstants";
import { PageSpinner } from "../../../../shared/Page";
import { DynamicGrid } from "../../../../dynamicGrid";

type UserRolesSubdrawerProps = {
  updateDrawerWidth: (width: string | undefined) => void;
  userId: string;
  updateRoleHierarchy: ReturnType<typeof useUpdateRoleHierarchy>;
};

export const UserRolesSubdrawer = ({ updateDrawerWidth, userId, updateRoleHierarchy }: UserRolesSubdrawerProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { badge } = useScimEditingMode();
  return (
    <>
      <Drawer.SubDrawerHeader
        onBackClick={() => navigate("..")}
        title={t("User.Grid.Drawer.Permissions.Label")}
        customSlot={badge}
        renderIcon={props => <PermissionIcon1 {...props} />}
      />
      <UserRolesGrid updateDrawerWidth={updateDrawerWidth} userId={userId} updateRoleHierarchy={updateRoleHierarchy} />
    </>
  );
};

type GridRow = {
  path: string[];
  roles: TenantRolesHierarchy["roles"];
  id: string;
  parentId: TenantRolesHierarchy["parentId"];
};

const UserRolesGrid = ({ updateDrawerWidth, userId, updateRoleHierarchy }: UserRolesSubdrawerProps) => {
  const { data: userRolesHierarchy } = useGetUserRolesHierarchy(userId);

  const { t } = useTranslation();
  const { isScimEnabled } = useScimEditingMode();

  useLayoutEffect(() => {
    const width = `calc(100% -  ${EXPANDED_SIDEBAR_WIDTH} - ${DynamicGrid.MAIN_COLUMN_WIDTH}px)`;
    updateDrawerWidth(width);

    return () => {
      updateDrawerWidth(undefined);
    };
  }, [updateDrawerWidth]);

  const handleRoleChange: OnRoleChange = ({ value, tenantId, role }) => {
    updateRoleHierarchy.mutate({
      userId: userId,
      payload: { tenantId, role, action: value ? "grant" : "revoke" },
    });
  };

  if (!userRolesHierarchy) return <PageSpinner />;

  const { tenantRolesHierarchy, editableRoles } = userRolesHierarchy;
  const rows = rolesHierarchyToGridRows([tenantRolesHierarchy]);

  const roleColumns = DISPLAYED_ROLES.map(columnRole =>
    roleColumn({
      columnRole,
      onRoleChange: handleRoleChange,
      width: getRoleGridColumnWidth(t(roleTexts[columnRole].labelKey)),
      isDisabled: ({ role, isInherited, parentId }) =>
        isScimEnabled ||
        isInherited ||
        updateRoleHierarchy.isPending ||
        !editableRoles.includes(role) ||
        Boolean(ROLES_CONTROLLED_BY_ROOT.includes(role) && parentId),
    })
  );

  return (
    <>
      {(() => {
        if (!updateRoleHierarchy.data || updateRoleHierarchy.data.fulfilled) return null;

        const { reason } = updateRoleHierarchy.data;
        if (reason === "NO_RESPONSIBLE_PERMISSION_ON_OTHER_ROLE") {
          return <UserResponsibleWarningDialog onClose={updateRoleHierarchy.reset} />;
        }

        if (reason === "LOSE_PERMISSION_TO_UPDATE_ROLES") {
          return (
            <LosePermissionToUpdateRolesDialog
              onCancel={updateRoleHierarchy.reset}
              onConfirm={() =>
                updateRoleHierarchy.mutateAsync({
                  ...updateRoleHierarchy.variables,
                  payload: { ...updateRoleHierarchy.variables.payload, force: true },
                })
              }
            />
          );
        }

        logUnhandled(reason, "reason");
      })()}

      <Drawer.Grid
        showColumnHeaders
        treeData
        getRowHeight={() => 48}
        getTreeDataPath={row => row.path}
        groupingColDef={groupingColDef({ headerName: t("UserSettings.RolesGrid.TenantHeader") })}
        isGroupExpandedByDefault={() => true}
        disableChildrenFiltering={false}
        columns={roleColumns}
        rows={rows}
      />
    </>
  );
};
const groupingColDef = ({ headerName }: { headerName: string }): DataGridPremiumProps["groupingColDef"] => ({
  renderHeader: () => (
    <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), fontWeight: "600" }}>{headerName}</Typography>
  ),
  minWidth: 300,
  renderCell: params => <TenantCell {...params} />,
});

const rolesHierarchyToGridRows = (rolesHierarchy: TenantRolesHierarchy[], path: string[] = []): GridRow[] =>
  rolesHierarchy.flatMap(({ organizationName, roles, id, children, parentId }) => {
    const newPath = [...path, organizationName];
    return [{ path: newPath, id, roles, parentId }, ...rolesHierarchyToGridRows(children ?? [], newPath)];
  });

type OnRoleChange = ({
  value,
  role,
  tenantId,
}: {
  value: boolean;
  role: Role;
  tenantId: TenantRolesHierarchy["id"];
}) => void;

const roleColumn = ({
  columnRole,
  onRoleChange,
  isDisabled,
  width,
}: {
  columnRole: DisplayedRole;
  onRoleChange: OnRoleChange;
  width: number;
  isDisabled: (params: { role: Role; isInherited: boolean; parentId: TenantRolesHierarchy["parentId"] }) => boolean;
}): GridColDef<GridRow> => ({
  width,
  field: columnRole,
  sortable: false,
  headerName: columnRole,
  headerAlign: "center",
  renderHeader: () => <RoleGridColumnHeader role={columnRole} />,
  renderCell: ({ row }) => {
    const userRole = row.roles.find(({ role }) => columnRole === role);
    const isInherited = Boolean(userRole?.inherited);
    return (
      <Stack sx={{ alignItems: "center", width: "100%", justifyContent: "center" }}>
        <Checkbox
          size="small"
          onChange={e => onRoleChange({ value: e.target.checked, role: columnRole, tenantId: row.id })}
          checked={Boolean(userRole)}
          disabled={isDisabled({ role: columnRole, isInherited, parentId: row.parentId })}
        />
      </Stack>
    );
  },
});

const TenantCell = (props: GridRenderCellParams) => {
  const { id, field, rowNode } = props;
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const numberOfDescendant = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector)[rowNode.id] ?? 0;
  const isExpanded = "childrenExpanded" in rowNode && rowNode.childrenExpanded;

  const handleClick = (event: React.MouseEvent) => {
    if (rowNode.type !== "group") return;

    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  return (
    <Stack direction="row" spacing={3} sx={{ alignItems: "center", ml: rowNode.depth * 4 }}>
      <IconButton
        aria-label={t(isExpanded ? "Common.Collapse" : "Common.Expand")}
        sx={{ color: theme => theme.palette.text.primary, visibility: numberOfDescendant > 0 ? "visible" : "hidden" }}
        onClick={handleClick}
      >
        <ChevronRightIcon rotate={isExpanded ? 90 : 0} size={12} />
      </IconButton>
      <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
        <TenantIcon2 size="sm" />
        <Typography variant="body2" sx={{ fontWeight: "600" }}>
          {"groupingKey" in rowNode && rowNode.groupingKey}
        </Typography>
      </Stack>
      <span />
    </Stack>
  );
};
